import angular from "angular";

import { ActionExecutor, ApiItemType, Dataset, DatasetListItem, Datasets, InputEnumOption, OpenAPIV3, UsecaseAction } from "@salaxy/core";

import { JsonSchemaService, SessionService, SessionUsecase, UiHelpers, UsecasesService } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { AjaxNg1 } from "../../ajax";

/**
 * Controller for listing, using and setting the data in Datasets:
 * Some arbitrary external data, typically Imports, Exports and Reports.
 */
export class DatasetsCrudController extends ApiCrudObjectController<Dataset, DatasetListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Datasets",
    "UiHelpers",
    "$location",
    "$routeParams",
    "UsecasesService",
    "JsonSchemaService",
    "SessionService",
    "AjaxNg1",
    "$timeout"
  ];

  /**
   * Type of component user interface.
   * Default is usecase if one is specified, otherwise "data-edit".
   * "full-edit" shows the full editor with data schema selections, adding new data objects etc.
   */
  public mode: "full-edit" | "data-edit" | "usecase" | "default";

  /**
   * The usecase definition if specified and loaded.
   * If the usecase has been checked from server, but none is defined, the value is "none".
   * For checking whether usecase really is available, check e.g. usecase.uri.
   */
  public usecase: null | "none" | SessionUsecase = null;

  /** Defines the default usecase (uri) that is used for new objects. */
  public defaultUsecase: string;

  /** Available usecases if the user needs to make a selection of which usecase to take. */
  public usecases: InputEnumOption[];

  /**
   * Creates a new DatasetsCrudController
   * @ignore
   */
  constructor(
    fullApi: Datasets,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private usecasesService: UsecasesService,
    private jsonSchemaService: JsonSchemaService,
    private sessionService: SessionService,
    private ajax: AjaxNg1,
    private $timeout: angular.ITimeoutService,
  ) {
    super(fullApi, uiHelpers, $location, $routeParams);
  }

  /**
   * Controller initialization
   */
  public $onInit() {
    super.$onInit();
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/datasets",
      detailsUrl: this.detailsUrl || "/datasets/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/Datasets.html",
      oDataOptions: {},
    };
  }

  /** Creates options for the tabs in the UI. */
  public generateTabOptions = function() {
    return this.uiHelpers.cache(this, "generateTabOptions", () => {
      if (!this.current || !this.current.data) {
        return [];
      }

      return this.current.data.map((table, index) => {
        return {
          value: table.name || `tab-${index}`,
          text: table.label || table.name,
        };
      });
    });
  };

  /** Overrides the reload to assure that usecase is also loaded. */
  public reload(): Promise<Dataset> {
    // TODO: Consider moving this functionality to base classes.
    return super.reload().then((result) => {
      if (!result) {
        this.usecase = "none";
        return result;
      }
      this.current.data.forEach((x: any) => {
        if ((x.schemaDoc || "any") != "any" && x.schemaDoc != "usecase") {
          // TODO: Assure that this loading does not happen several times for same URL.
          this.jsonSchemaService.assureSchemaDocument(x.schemaDoc);
        }
      });
      this.generateTabOptions();
      const usecaseUri = this.$location?.search().usecase || this.defaultUsecase;
      result.data.forEach((x) => x.content = x.content || []);
      if (usecaseUri || result.usecase?.uri) {
        return this.usecasesService.assureUsecase(ApiItemType.Dataset, result, usecaseUri).then((usecase) => {
          if (usecase) {
            this.usecase = usecase;
          } else {
            this.usecase = "none";
            this.assureUsecasesLoaded();
          }
          return result;
        });
      } else {
        this.assureUsecasesLoaded();
        this.usecase = "none";
        return result;
      }
    });
  }

  private assureUsecasesLoaded() {
    this.usecasesService.getActiveUsecaseListItems(ApiItemType.Dataset).then((list) => {
      this.usecases = list.sort((a, b) => (a.data.order || 100) - (b.data.order || 100))
        .map((x) => ({ text: x.shortText, value: x.uri }));
    });
  }

  /** Gets the user interface mode */
  public getMode(): "data-edit" | "full-edit" | "usecase" | "loading" {
    if (this.isLoading || (!(this.usecase as SessionUsecase)?.isLoaded && this.usecase != "none")) {
      // TODO: Check the case when dataset has no usecase. Is it infinite loader now?
      return "loading";
    }
    switch (this.mode) {
      case "data-edit":
      case "full-edit":
      case "usecase":
        return this.mode;
      default:
        if ((this.usecase as SessionUsecase)?.defaultView) {
          return "usecase";
        }
        return "data-edit";
    }
  }

  /** Adds a new "table" to the dataset */
  public addDataObject() {
    this.current.data.push({
      label: "Uusi tietue"
    });
  }

  /**
   * Removes a data object from the dataset
   * @param index Index of the object to remove.
   */
  public deleteObject(index: number) {
    // TODO: Add "Are you sure?"
    this.current.data.splice(index, 1);
  }

  /** Executes the current action with the current item. */
  public async executeAction(action: UsecaseAction): Promise<ActionExecutor> {
    const executor = new ActionExecutor(action);
    await executor.execute(this.current, this.sessionService.session, this.ajax);
    if (executor.isAsync) {
      this.$timeout();
    }
    return executor;
  }

  /**
   * Adds an item to the default view (first view). Adds the view if it does not already exist.
   * Method may later be extended to support multiple views and/or more elaborate column properties.
   * @param dataObjectIndex Index of the data object to which the view item is added.
   * @param type Add include or exclude rule.
   * @param value Value to add to the array.
   */
  public addViewItem(dataObjectIndex: number, type: "include" | "exclude", value = "") {
    const dataObj = this.current.data[dataObjectIndex];
    dataObj.views = dataObj.views || [];
    if (dataObj.views.length < 1) {
      dataObj.views.push({});
    }
    const view = dataObj.views[0];
    if (type == "exclude") {
      view.exclude = view.exclude || [];
      view.exclude.push(value);
    } else if (type == "include") {
      view.include = view.include || [];
      view.include.push(value);
    }
  }

  /**
   * Helper method to move an item in an array up or down.
   * @param arr Array to operate on
   * @param fromIndex Index of the item to move
   * @param step How many items to move and to which direction: -1 is one up, 1 is one down.
   */
  public arraymove(arr: [], fromIndex: number, step: number) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    let toIndex = fromIndex + step;
    if (toIndex > arr.length) {
      toIndex = arr.length;
    }
    if (toIndex < 0) {
      toIndex = 0;
    }
    arr.splice(toIndex, 0, element);
  }

  /**
   * Gets the available schema documents that can be used for modeling the data objects.
   * @returns Cached collection of available schema documents.
   */
  public getSchemaDocs(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "schemaList", () => {
      console.log("Refresh cache.");
      return [
        {
          value: "any",
          text: "Ei määritetty (any)",
          title: "any",
        },
        {
          value: "usecase",
          text: "Räätälöinti",
          title: "usecase",
        },
        ...this.jsonSchemaService.getSchemas().map((x) => ({
          value: x.url,
          text: x.doc.info.title,
          title: x.url,
        }))
      ];
    }, () => this.jsonSchemaService.getSchemas().map((x) => x.url));
  }

  /**
   * Gets the schemas defined in the specified schema document
   * @param docUrl The URL (unique key in cache) for the schema document.
   */
  public getSchemas(docUrl): InputEnumOption[] {
    if (!docUrl || docUrl == "any") {
      docUrl = "";
    }
    if (!this.schemaCache[docUrl]) {
      if (docUrl == "usecase") {
        if (this.usecase == "none") {
          this.schemaCache.usecase = [];
        } else {
          const schemas = this.usecase.schema.components.schemas;
          this.schemaCache[docUrl] = Object.keys(schemas).map((key) => ({
            value: key,
            text: `${schemas[key].title} (${key})`,
            title: schemas[key].description || key,
          }));
        }
      } else {
        const schemas = this.jsonSchemaService.findSchemaDoc(docUrl, true).doc.components.schemas;
        this.schemaCache[docUrl] = Object.keys(schemas).map((key) => {
          const text = (schemas[key] as OpenAPIV3.SchemaObject).title ? `${(schemas[key] as OpenAPIV3.SchemaObject).title} (${key})` : key;
          return {
            value: key,
            text,
            title: (schemas[key] as OpenAPIV3.SchemaObject).description || key,
          };
        });
      }
    }
    return this.schemaCache[docUrl];
  }

  /**
   * Gets the full schema definition for the specified table.
   * @param index Index number of the data object in the data collection.
   */
  public getSchamaDef(index: number): OpenAPIV3.SchemaObject {
    const docUrl = (this.current.data[index] as any).schemaDoc;
    const schemaKey = this.current.data[index].schema;
    if (!docUrl || docUrl == "any") {
      return null;
    }
    if (docUrl == "usecase") {
      if (this.usecase == "none") {
        return null;
      } else {
        return this.usecase.schema.components.schemas[schemaKey];
      }
    } else {
      const schemaDoc = this.jsonSchemaService.findSchemaDoc(docUrl, false);
      return schemaDoc ? schemaDoc.doc.components.schemas[schemaKey] as OpenAPIV3.SchemaObject : null;
    }
  }

  /**
   * Gets all the properties within a schema for property selection.
   * @param index Index number of the data object in the data collection.
   */
  public getAllPropertiesOfSchema(index: number): InputEnumOption[] {
    const docUrl = (this.current.data[index] as any).schemaDoc;
    const schemaKey = this.current.data[index].schema;
    if (this.propertyTreeCache[docUrl + schemaKey]) {
      return this.propertyTreeCache[docUrl + schemaKey];
    }
    const result = this.getProperties(this.getSchamaDef(index));
    this.propertyTreeCache[docUrl + schemaKey] = result;
    return result;
  }

  private getProperties(schema: OpenAPIV3.SchemaObject, prefix = ""): InputEnumOption[] {
    const result: InputEnumOption[] = [];
    Object.keys(schema.properties || {}).forEach(key => {
      const prop = schema.properties[key] as OpenAPIV3.SchemaObject;
      result.push({
        value: prefix + key,
        text: prefix + key,
        title: prop.title || prop.description,
      });
      if (prop.type == "object") {
        result.push(...this.getProperties(prop as OpenAPIV3.SchemaObject, prefix + key + "."));
      }
    });
    return result;
  }

  private schemaCache: { [key: string]: InputEnumOption[] } = {
    "": [],
  };

  private propertyTreeCache: { [key: string]: InputEnumOption[] } = {
    "": [],
  };

}
